import { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Grid, Hidden, LinearProgress } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useHistory } from 'react-router';
import { Formik } from 'formik';
import { MFSubmitButton, MFTextField } from '../../lib/formik';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { DatePicker } from '../../lib/formik/DatePicker';
import { useDispatch } from 'react-redux';
import { SubHeading } from '../investors/components';
import { addPlan, fundAccess, getPlanDetail, updatePlan } from '../../redux-store/actions/funds';
import { accessType, addPlanType, ClassPlanProps } from '../../redux-store/types/funds';
import MFSelectField from '../../lib/formik/SelectField';
import { Location } from 'history';
import { getFundManagersList, getTrustessList } from '../../redux-store/actions/userManagement';
import { DistributorListType, FundManager, Trustee } from '../../redux-store/types/api-types';
import { addPlanValidation } from '../../utils/schema';
import {
  allowOnlyNumbers,
  currencyConversion,
  isAccreditedInvestorApplicable,
  isFieldApplicable,
  isFieldVariable,
  getArrayLength,
  makeStringInSingleLine,
  isFieldWord,
  isFieldFixed,
} from '../../utils/utilityFunctions';
import {
  CurrencyMaster,
  FEE_TYPE_MASTER,
  FEE_TYPE_WORD_MASTER,
  YES_NO_MASTER,
  defaultCurrency,
} from '../../utils/constant';
import { styles } from '../../Styles/styles';
import { MultipleSelect } from '../../lib/formik/MultipleSelectField';
import { getDistributorsListForFilters } from '../../redux-store/actions';
import MFCheckbox from '../../lib/formik/Checkbox';
import UseRadioGroup from '../../lib/formik/Radio';

type NewPlanType = addPlanType & {
  isApplicableForDistributor: boolean;
};
const initialValues: NewPlanType = {
  // id: '',
  schemeId: 0,
  planCode: '',
  planDescription: '',
  schemeNature: '',
  registrationNumber: '',
  schemeCategory: '',
  country: '',
  currency: defaultCurrency,
  transactionCurrency: defaultCurrency,
  schemeStartDate: `${new Date()}`,
  lockInPeriod: '',
  lockInExtension: '',
  faceValue: 0,
  fundAddress1: '',
  fundmanagerIds: null,
  fundmanagerDetails: { fundmanagerIds: [], fundmanagers: [] },
  trusteeDetails: { trusteeIds: [], trustees: [] },
  trusteeIds: null,
  requiredTrustees: null,
  requiredFundManagers: null,
  fundAddress2: '',
  fundAddress3: '',
  inActive: true,
  isActive: true,
  custodian: '',
  // setupFee: 0,
  minCommitmentAmount: null,
  maxCommitmentAmount: 0,
  minContributionPercentage: 0,
  defaultManagementFee: 0,
  tcLink: '',
  distributorIds: null,
  isApplicableForDirect: false,
  isApplicableForDistributor: false,
  accreditedInvestorApplicable: 'no',
  accreditedMinCommitmentAmount: null,
  managementFeeApplicable: 'no',
  managementFeeType: '',
  maxManagementFee: null,
  minManagementFee: null,
  hurdleRateApplicable: 'no',
  hurdleRateType: '',
  defaultHurdleRate: null,
  minHurdleRate: null,
  maxHurdleRate: null,
  performanceFeeApplicable: 'no',
  performanceFeeType: '',
  defaultPerformanceFee: null,
  minPerformanceFee: null,
  maxPerformanceFee: null,
  exitChargeApplicable: 'no',
  exitChargeType: '',
  defaultExitCharge: null,
  minExitCharge: null,
  maxExitCharge: null,
  setupFeeApplicable: 'no',
  setupFeeType: '',
  maxSetupFee: null,
  minSetupFee: null,
  setupFee: null,
  placementFeeApplicable: 'no',
  placementFeeType: '',
  maxPlacementFee: null,
  minPlacementFee: null,
  defaultPlacementFee: null,
  operatingExpenseApplicable: 'no',
  operatingExpenseType: '',
  maxOperatingExpense: null,
  minOperatingExpense: null,
  defaultOperatingExpenses: null,
  exitLoad: null,
  exitLoadApplicable: 'no',
  setupFeeInWords: '',
  managementFeeInWords: '',
  operatingExpenseInWords: '',
};

export const Plan = ({
  values,
  access,
  fundManager,
  trustee,
  distributorList,
  setValues,
}: {
  values: NewPlanType;
  access: boolean;
  fundManager: FundManager[];
  trustee: Trustee[];
  distributorList: DistributorListType[];
  setValues: (values: NewPlanType) => void;
}): JSX.Element => {
  return (
    <Grid container rowSpacing={1} columnSpacing={4} px={2}>
      <SubHeading
        sx={{
          color: 'common.blue',
          letterSpacing: 0.5 + 'px',
          padding: { xs: '10px 10px', sm: '10px 30px' },
          ml: { xs: 3.5 },
        }}>
        {'Plan Details'}
      </SubHeading>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField name="planCode" label="Plan Code *" placeholder="Enter Plan Code" />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="planDescription"
          label="Plan Description *"
          placeholder="Enter Plan Description"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="schemeNature"
          label="Scheme Nature *"
          placeholder="Enter Scheme Nature"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="schemeCategory"
          label="Scheme Category *"
          placeholder="Enter Scheme Category"
        />
      </Grid>
      {/* <Grid item xs={12} sm={6} md={4}>
                    <MFTextField
                      name="registrationNumber"
                      label="Registration Number *"
                      placeholder="Enter Registration Number"
                    />
                  </Grid> */}
      <Grid item xs={12} sm={6} md={4}>
        <MultipleSelect
          name="fundmanagerIds"
          label="Investment Manager(s) *"
          items={fundManager.map((item) => ({
            key: item.id,
            value: item.name,
          }))}
          renderText="Selected Investment Manager(s)"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFSelectField
          name="requiredFundManagers"
          label="No. of Investment Manager(s) to esign *"
          items={getArrayLength(values?.fundmanagerIds?.length || 1)?.map((thresholdValue) => ({
            key: thresholdValue,
            value: thresholdValue,
          }))}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MultipleSelect
          name="trusteeIds"
          label="Trustee Name(s) *"
          items={trustee.map((item) => ({
            key: item.id,
            value: item.name,
          }))}
          renderText="Selected Trustee(s)"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFSelectField
          name="requiredTrustees"
          label="No. of Trustee(s) to esign *"
          items={getArrayLength(values?.trusteeIds?.length || 1)?.map((thresholdValue) => ({
            key: thresholdValue,
            value: thresholdValue,
          }))}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField name="country" label="Country" placeholder="Enter Country" />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFSelectField
          name="currency"
          label="Currency"
          items={Object.keys(CurrencyMaster).map((item) => ({
            key: CurrencyMaster[item].currency,
            value: item,
          }))}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFSelectField
          name="transactionCurrency"
          label="Transaction Currency"
          items={Object.keys(CurrencyMaster).map((item) => ({
            key: CurrencyMaster[item].currency,
            value: item,
          }))}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <DatePicker
          label={'Scheme Start Date'}
          inputLabelStyles={{
            transform: 'unset',

            fontSize: 14,
            fontWeight: 500,
            color: 'rgba(0,0,0,0.7)',
          }}
          placeholder={'Enter Scheme Start Date'}
          // maxDate={minDateForContributor()}
          name={`schemeStartDate`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="lockInPeriod"
          label="Lock In Period(In Year)"
          placeholder="Enter Lock In Period(In Year)"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="lockInExtension"
          label="Lock In Extension(In Year)"
          disabled={!access}
          placeholder="Enter Lock In Extension(In Year)"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="faceValue"
          label="Face value"
          placeholder="Enter Face value"
          type="number"
          onKeyDown={(e) => {
            allowOnlyNumbers(e);
          }}
          trimOnBlur={false}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="fundAddress1"
          label="Fund Address 1"
          placeholder="Enter Fund Address 1"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="fundAddress2"
          label="Fund Address 2"
          placeholder="Enter Fund Address 2"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="fundAddress3"
          label="Fund Address 3"
          placeholder="Enter Fund Address 3"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField name="custodian" label="Custodian" placeholder="Enter Custodian" />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <UseRadioGroup
          formLabel="Setup Fee Applicable *"
          name={`setupFeeApplicable`}
          items={Object.keys(YES_NO_MASTER).map((item) => ({
            label: YES_NO_MASTER[item],
            value: item,
          }))}
        />
      </Grid>
      {isFieldApplicable(values.setupFeeApplicable) && (
        <Grid item xs={12} sm={6} md={4}>
          <UseRadioGroup
            formLabel="Setup Fee Type *"
            name={`setupFeeType`}
            items={Object.keys(FEE_TYPE_WORD_MASTER).map((item) => ({
              label: FEE_TYPE_WORD_MASTER[item],
              value: item,
            }))}
          />
        </Grid>
      )}
      {(isFieldFixed(values.setupFeeType, values.setupFeeApplicable) ||
        isFieldVariable(values.setupFeeType, values.setupFeeApplicable)) && (
        <Grid item xs={12} sm={6} md={4}>
          <MFTextField
            name="setupFee"
            label="Setup Fee *"
            placeholder="Enter Setup Fee"
            type="number"
            onKeyDown={(e) => allowOnlyNumbers(e)}
            trimOnBlur={false}
          />
        </Grid>
      )}
      {isFieldVariable(values.setupFeeType, values.setupFeeApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="minSetupFee"
              label="Minimum Setup Fee *"
              placeholder="Enter Minimum Setup Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="maxSetupFee"
              label="Maximum Setup Fee *"
              placeholder="Enter Maximum Setup Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      {isFieldWord(values.setupFeeType, values.setupFeeApplicable) && (
        <Grid item xs={12} sm={6} md={4}>
          <MFTextField name="setupFeeInWords" label="Setup Fee *" placeholder="Enter Setup Fee" />
        </Grid>
      )}
      {/* <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="setupFee"
          label="Setup Fee"
          placeholder="Enter Setup Fee"
          type="number"
          onKeyDown={(e) => {
            allowOnlyNumbers(e);
          }}
          trimOnBlur={false}
        />
      </Grid> */}
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="minCommitmentAmount"
          label="Minimum Commitment Amount *"
          placeholder="Enter Minimum Commitment Amount"
          type="number"
          onKeyDown={(e) => {
            allowOnlyNumbers(e);
          }}
          trimOnBlur={false}
        />
        {values.minCommitmentAmount != 0 && (
          <Typography sx={{ fontSize: 13 }}>
            {currencyConversion(values.minCommitmentAmount, values.currency)}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="maxCommitmentAmount"
          label="Maximum Commitment Amount *"
          placeholder="Enter Maximum Commitment Amount"
          type="number"
          onKeyDown={(e) => {
            allowOnlyNumbers(e);
          }}
          trimOnBlur={false}
        />
        {values.maxCommitmentAmount != 0 && (
          <Typography sx={{ fontSize: 13 }}>
            {currencyConversion(values.maxCommitmentAmount, values.currency)}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField
          name="minContributionPercentage"
          label="Minimum Contribution Percentage"
          placeholder="Enter Minimum Contribution Percentage"
          type="number"
          onKeyDown={(e) => {
            allowOnlyNumbers(e);
          }}
          trimOnBlur={false}
          disabled
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <MFTextField name="tcLink" label={`T&C Link`} placeholder={`Enter T&C Link`} />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <UseRadioGroup
          formLabel="Accredited Investor Applicable *"
          name={`accreditedInvestorApplicable`}
          items={Object.keys(YES_NO_MASTER).map((item) => ({
            label: YES_NO_MASTER[item],
            value: item,
          }))}
          disabled
        />
      </Grid>
      {isAccreditedInvestorApplicable(values.accreditedInvestorApplicable) && (
        <Grid item xs={12} sm={6} md={4}>
          <MFTextField
            name="accreditedMinCommitmentAmount"
            label="Accredited Minimum Commitment Amount *"
            placeholder="Enter Accredited Minimum Commitment Amount"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
          />
          {values.accreditedMinCommitmentAmount != 0 && (
            <Typography sx={{ fontSize: 13 }}>
              {currencyConversion(values.accreditedMinCommitmentAmount, values?.currency)}
            </Typography>
          )}
        </Grid>
      )}
      <Grid item xs={12} sm={6} md={4}>
        <UseRadioGroup
          formLabel="Management Fee Applicable *"
          name={`managementFeeApplicable`}
          items={Object.keys(YES_NO_MASTER).map((item) => ({
            label: YES_NO_MASTER[item],
            value: item,
          }))}
        />
      </Grid>
      {isFieldApplicable(values.managementFeeApplicable) && (
        <Grid item xs={12} sm={6} md={4}>
          <UseRadioGroup
            formLabel="Management Fee Type *"
            name={`managementFeeType`}
            items={Object.keys(FEE_TYPE_WORD_MASTER).map((item) => ({
              label: FEE_TYPE_WORD_MASTER[item],
              value: item,
            }))}
          />
        </Grid>
      )}
      {(isFieldFixed(values.managementFeeType, values.managementFeeApplicable) ||
        isFieldVariable(values.managementFeeType, values.managementFeeApplicable)) && (
        <Grid item xs={12} sm={6} md={4}>
          <MFTextField
            name="defaultManagementFee"
            label="Management Fee *"
            placeholder="Enter Management Fee"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
          />
        </Grid>
      )}
      {isFieldVariable(values.managementFeeType, values.managementFeeApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="minManagementFee"
              label="Minimum Management Fee *"
              placeholder="Enter Minimum Management Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="maxManagementFee"
              label="Maximum Management Fee *"
              placeholder="Enter Maximum Management Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      {isFieldWord(values.managementFeeType, values.managementFeeApplicable) && (
        <Grid item xs={12} sm={6} md={4}>
          <MFTextField
            name="managementFeeInWords"
            label="Management Fee *"
            placeholder="Enter Management Fee"
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6} md={4}>
        <UseRadioGroup
          formLabel="Hurdle Rate Applicable *"
          name={`hurdleRateApplicable`}
          items={Object.keys(YES_NO_MASTER).map((item) => ({
            label: YES_NO_MASTER[item],
            value: item,
          }))}
          disabled
        />
      </Grid>
      {isFieldApplicable(values.hurdleRateApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <UseRadioGroup
              formLabel="Hurdle Rate Type *"
              name={`hurdleRateType`}
              items={Object.keys(FEE_TYPE_MASTER).map((item) => ({
                label: FEE_TYPE_MASTER[item],
                value: item,
              }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="defaultHurdleRate"
              label="Hurdle Rate *"
              placeholder="Enter Hurdle Rate"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      {isFieldVariable(values.hurdleRateType, values.hurdleRateApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="minHurdleRate"
              label="Minimum Hurdle Rate *"
              placeholder="Enter Minimum Hurdle Rate"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="maxHurdleRate"
              label="Maximum Hurdle Rate *"
              placeholder="Enter Maximum Hurdle Rate"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={6} md={4}>
        <UseRadioGroup
          formLabel="Performance Fee Applicable *"
          name={`performanceFeeApplicable`}
          items={Object.keys(YES_NO_MASTER).map((item) => ({
            label: YES_NO_MASTER[item],
            value: item,
          }))}
          disabled
        />
      </Grid>
      {isFieldApplicable(values.performanceFeeApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <UseRadioGroup
              formLabel="Performance Fee Type *"
              name={`performanceFeeType`}
              items={Object.keys(FEE_TYPE_MASTER).map((item) => ({
                label: FEE_TYPE_MASTER[item],
                value: item,
              }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="defaultPerformanceFee"
              label="Performance Fee *"
              placeholder="Enter Performance Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      {isFieldVariable(values.performanceFeeType, values.performanceFeeApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="minPerformanceFee"
              label="Minimum Performance Fee *"
              placeholder="Enter Minimum Performance Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="maxPerformanceFee"
              label="Maximum Performance Fee *"
              placeholder="Enter Maximum Performance Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={6} md={4}>
        <UseRadioGroup
          formLabel="Exit Charge Applicable *"
          name={`exitChargeApplicable`}
          items={Object.keys(YES_NO_MASTER).map((item) => ({
            label: YES_NO_MASTER[item],
            value: item,
          }))}
          disabled
        />
      </Grid>
      {isFieldApplicable(values.exitChargeApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <UseRadioGroup
              formLabel="Exit Charge Type *"
              name={`exitChargeType`}
              items={Object.keys(FEE_TYPE_MASTER).map((item) => ({
                label: FEE_TYPE_MASTER[item],
                value: item,
              }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="defaultExitCharge"
              label="Exit Charge *"
              placeholder="Enter Exit Charge"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      {isFieldVariable(values.exitChargeType, values.exitChargeApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="minExitCharge"
              label="Minimum Exit Charge *"
              placeholder="Enter Minimum Exit Charge"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="maxExitCharge"
              label="Maximum Exit Charge *"
              placeholder="Enter Maximum Exit Charge"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={6} md={4}>
        <UseRadioGroup
          formLabel="Placement Fee Applicable *"
          name={`placementFeeApplicable`}
          items={Object.keys(YES_NO_MASTER).map((item) => ({
            label: YES_NO_MASTER[item],
            value: item,
          }))}
          disabled
        />
      </Grid>
      {isFieldApplicable(values.placementFeeApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <UseRadioGroup
              formLabel="Placement Fee Type *"
              name={`placementFeeType`}
              items={Object.keys(FEE_TYPE_MASTER).map((item) => ({
                label: FEE_TYPE_MASTER[item],
                value: item,
              }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="defaultPlacementFee"
              label="Placement Fee *"
              placeholder="Enter Placement Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      {isFieldVariable(values.placementFeeType, values.placementFeeApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="minPlacementFee"
              label="Minimum Placement Fee *"
              placeholder="Enter Minimum Placement Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="maxPlacementFee"
              label="Maximum Placement Fee *"
              placeholder="Enter Placement Setup Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} sm={6} md={4}>
        <UseRadioGroup
          formLabel="Operating Expenses Applicable *"
          name={`operatingExpenseApplicable`}
          items={Object.keys(YES_NO_MASTER).map((item) => ({
            label: YES_NO_MASTER[item],
            value: item,
          }))}
        />
      </Grid>
      {isFieldApplicable(values.operatingExpenseApplicable) && (
        <Grid item xs={12} sm={6} md={4}>
          <UseRadioGroup
            formLabel="Operating Expenses Type *"
            name={`operatingExpenseType`}
            items={Object.keys(FEE_TYPE_WORD_MASTER).map((item) => ({
              label: FEE_TYPE_WORD_MASTER[item],
              value: item,
            }))}
          />
        </Grid>
      )}
      {(isFieldFixed(values.operatingExpenseType, values.operatingExpenseApplicable) ||
        isFieldVariable(values.operatingExpenseType, values.operatingExpenseApplicable)) && (
        <Grid item xs={12} sm={6} md={4}>
          <MFTextField
            name="defaultOperatingExpenses"
            label="Operating Expenses *"
            placeholder="Enter Operating Expenses"
            type="number"
            onKeyDown={(e) => {
              allowOnlyNumbers(e);
            }}
            trimOnBlur={false}
          />
        </Grid>
      )}
      {isFieldVariable(values.operatingExpenseType, values.operatingExpenseApplicable) && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="minOperatingExpense"
              label="Minimum Operating Expenses *"
              placeholder="Enter Minimum Operating Expenses"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <MFTextField
              name="maxOperatingExpense"
              label="Maximum Operating Fee *"
              placeholder="Enter Maximum Operating Fee"
              type="number"
              onKeyDown={(e) => {
                allowOnlyNumbers(e);
              }}
              trimOnBlur={false}
            />
          </Grid>
        </>
      )}
      {isFieldWord(values.operatingExpenseType, values.operatingExpenseApplicable) && (
        <Grid item xs={12} sm={6} md={4}>
          <MFTextField
            name="operatingExpenseInWords"
            label="Operating Expense *"
            placeholder="Enter Operating Expense"
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6} md={4}>
        <UseRadioGroup
          formLabel="Exit Load Applicable *"
          name={`exitLoadApplicable`}
          items={Object.keys(YES_NO_MASTER).map((item) => ({
            label: YES_NO_MASTER[item],
            value: item,
          }))}
          disabled
        />
      </Grid>
      {isFieldApplicable(values.exitLoadApplicable) && (
        <Grid item xs={12} sm={6} md={4}>
          <MFTextField
            name="exitLoad"
            multiline
            rows={4.5}
            label={`Exit Load *`}
            placeholder={`Enter Exit Load`}
          />
        </Grid>
      )}
      {/* empty grid for proper alignment */}
      <Grid xs={12}></Grid>
      <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex' }}>
          <MFCheckbox name={`isApplicableForDirect`} label="Direct" />
          <MFCheckbox
            name={`isApplicableForDistributor`}
            label="Distributor"
            onChange={({ target: { checked } }) => {
              setValues({
                ...values,
                isApplicableForDistributor: checked,
                distributorIds: [],
              });
            }}
            checked={values.isApplicableForDistributor}
          />
        </Box>
        {values.isApplicableForDistributor && (
          <MultipleSelect
            name="distributorIds"
            label="Applicable Distributor(s) *"
            items={distributorList.map((item) => ({
              key: item.id,
              value: `${item.arnCode || 'N/A'} - ${item.name} (${item.type})`,
            }))}
            renderText="Selected Distributor(s)"
          />
        )}
      </Grid>{' '}
    </Grid>
  );
};

export default function AddPlan({
  location,
}: {
  location: Location<{ fundId: string; PlanId: string }>;
}): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();

  const [fundManager, setFundManager] = useState<FundManager[]>([]);
  const [loading, setLoading] = useState(false);
  const [planInitial, setPlanInitial] = useState(initialValues);
  const [access, setAccess] = useState(false);
  const { fundId, PlanId } = location.state || {};
  const [trustee, setTrustee] = useState<Trustee[]>([]);
  const [distributorList, setDistributorList] = useState<DistributorListType[]>([]);

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        setLoading(true);
        const trusteeList = (await dispatch(getTrustessList())) as unknown as Trustee[];
        const fundManagerList = (await dispatch(getFundManagersList())) as unknown as FundManager[];
        const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
        const distributorLists = (await dispatch(
          getDistributorsListForFilters({ isActive: true })
        )) as unknown as DistributorListType[];
        setAccess(fundSetupAccess);
        setFundManager(fundManagerList?.filter((fundManager) => fundManager?.name));
        setTrustee(trusteeList.filter((trustee) => trustee?.name));
        setDistributorList(distributorLists);
        if (PlanId) {
          const GetFundDetail = (await dispatch(
            getPlanDetail(PlanId)
          )) as unknown as ClassPlanProps;
          const {
            planCode,
            schemeId,
            planDescription,
            schemeNature,
            registrationNumber,
            schemeCategory,
            country,
            currency,
            schemeStartDate,
            lockInPeriod,
            lockInExtension,
            inActive,
            faceValue,
            fundAddress1,
            fundAddress2,
            fundAddress3,
            isActive,
            custodian,
            setupFee,
            minCommitmentAmount,
            maxCommitmentAmount,
            // eslint-disable-next-line
            minContributionPercentage,
            defaultManagementFee,
            fundmanagerDetails,
            trusteeDetails,
            requiredTrustees,
            requiredFundManagers,
            tcLink,
            distributorDetails,
            isApplicableForDirect,
            // eslint-disable-next-line
            accreditedInvestorApplicable,
            accreditedMinCommitmentAmount,
            // eslint-disable-next-line
            managementFeeApplicable,
            managementFeeType,
            maxManagementFee,
            minManagementFee,
            // eslint-disable-next-line
            hurdleRateApplicable,
            hurdleRateType,
            defaultHurdleRate,
            minHurdleRate,
            maxHurdleRate,
            // eslint-disable-next-line
            performanceFeeApplicable,
            performanceFeeType,
            defaultPerformanceFee,
            minPerformanceFee,
            maxPerformanceFee,
            // eslint-disable-next-line
            exitChargeApplicable,
            exitChargeType,
            defaultExitCharge,
            minExitCharge,
            maxExitCharge,
            // eslint-disable-next-line
            setupFeeApplicable,
            setupFeeType,
            maxSetupFee,
            minSetupFee,
            // eslint-disable-next-line
            placementFeeApplicable,
            placementFeeType,
            maxPlacementFee,
            minPlacementFee,
            defaultPlacementFee,
            // eslint-disable-next-line
            operatingExpenseApplicable,
            operatingExpenseType,
            maxOperatingExpense,
            minOperatingExpense,
            defaultOperatingExpenses,
            exitLoad,
            // eslint-disable-next-line
            exitLoadApplicable,
            setupFeeInWords,
            managementFeeInWords,
            operatingExpenseInWords,
            transactionCurrency,
          } = GetFundDetail || {};

          setPlanInitial({
            ...planInitial,
            planCode,
            schemeId,
            planDescription,
            schemeNature,
            registrationNumber,
            schemeCategory,
            country,
            currency: currency?.toLowerCase() || defaultCurrency,
            schemeStartDate,
            lockInPeriod,
            lockInExtension,
            inActive,
            faceValue,
            fundAddress1,
            fundAddress2,
            fundAddress3,
            isActive,
            custodian,
            setupFee,
            minCommitmentAmount,
            maxCommitmentAmount,
            minContributionPercentage: 0,
            defaultManagementFee,
            fundmanagerIds: (
              fundmanagerDetails?.fundmanagerIds ||
              planInitial.fundmanagerIds ||
              []
            )?.map((item) => Number(item)),
            trusteeIds: (trusteeDetails?.trusteeIds || planInitial.trusteeIds || [])?.map((item) =>
              Number(item)
            ),
            requiredTrustees: requiredTrustees || planInitial.requiredTrustees,
            requiredFundManagers: requiredFundManagers || planInitial.requiredFundManagers,
            tcLink,
            distributorIds: distributorDetails.distributorIds || null,
            isApplicableForDirect: isApplicableForDirect || false,
            isApplicableForDistributor: distributorDetails.distributorIds.length > 0 ? true : false,
            accreditedInvestorApplicable: 'no',
            accreditedMinCommitmentAmount,
            managementFeeApplicable,
            managementFeeType,
            maxManagementFee,
            minManagementFee,
            hurdleRateApplicable: 'no',
            hurdleRateType,
            defaultHurdleRate,
            minHurdleRate,
            maxHurdleRate,
            performanceFeeApplicable: 'no',
            performanceFeeType,
            defaultPerformanceFee,
            minPerformanceFee,
            maxPerformanceFee,
            exitChargeApplicable: 'no',
            exitChargeType,
            defaultExitCharge,
            minExitCharge,
            maxExitCharge,
            setupFeeApplicable,
            setupFeeType,
            maxSetupFee,
            minSetupFee,
            placementFeeApplicable: 'no',
            placementFeeType,
            maxPlacementFee,
            minPlacementFee,
            defaultPlacementFee,
            operatingExpenseApplicable,
            operatingExpenseType,
            maxOperatingExpense,
            minOperatingExpense,
            defaultOperatingExpenses,
            exitLoad,
            exitLoadApplicable: 'no',
            setupFeeInWords,
            managementFeeInWords,
            operatingExpenseInWords,
            transactionCurrency: transactionCurrency || defaultCurrency,
          });
        }
        if (!isComponentAlive) return;
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (isComponentAlive) {
          setLoading(false);
        }
      }
    })();
    return () => {
      isComponentAlive = false;
    };
  }, []);

  const onSubmit = async (values: NewPlanType) => {
    try {
      const {
        planCode,
        schemeId,
        planDescription,
        schemeNature,
        registrationNumber,
        schemeCategory,
        country,
        currency,
        schemeStartDate,
        lockInPeriod,
        lockInExtension,
        inActive,
        faceValue,
        fundAddress1,
        fundAddress2,
        fundAddress3,
        isActive,
        custodian,
        setupFee,
        minCommitmentAmount,
        maxCommitmentAmount,
        minContributionPercentage,
        defaultManagementFee,
        fundmanagerIds,
        trusteeIds,
        requiredTrustees,
        requiredFundManagers,
        tcLink,
        distributorIds,
        isApplicableForDirect,
        accreditedInvestorApplicable,
        accreditedMinCommitmentAmount,
        managementFeeApplicable,
        managementFeeType,
        maxManagementFee,
        minManagementFee,
        hurdleRateApplicable,
        hurdleRateType,
        defaultHurdleRate,
        minHurdleRate,
        maxHurdleRate,
        performanceFeeApplicable,
        performanceFeeType,
        defaultPerformanceFee,
        minPerformanceFee,
        maxPerformanceFee,
        exitChargeApplicable,
        exitChargeType,
        defaultExitCharge,
        minExitCharge,
        maxExitCharge,
        setupFeeApplicable,
        setupFeeType,
        maxSetupFee,
        minSetupFee,
        placementFeeApplicable,
        placementFeeType,
        maxPlacementFee,
        minPlacementFee,
        defaultPlacementFee,
        operatingExpenseApplicable,
        operatingExpenseType,
        maxOperatingExpense,
        minOperatingExpense,
        defaultOperatingExpenses,
        exitLoad,
        exitLoadApplicable,
        setupFeeInWords,
        managementFeeInWords,
        operatingExpenseInWords,
        transactionCurrency,
      } = values;
      const updatedFundmanagerIds =
        fundmanagerIds?.sort((id1, id2) => Number(id1) - Number(id2))?.map((f) => Number(f)) || [];
      const updatedTrusteeIds =
        trusteeIds?.sort((id1, id2) => Number(id1) - Number(id2))?.map((f) => Number(f)) || [];
      const updatedValues = {
        schemeId: !PlanId ? fundId : schemeId,
        planCode,
        planDescription,
        schemeNature,
        registrationNumber,
        schemeCategory,
        country,
        currency,
        schemeStartDate,
        lockInPeriod,
        lockInExtension,
        fundmanagerIds: updatedFundmanagerIds,
        trusteeIds: updatedTrusteeIds,
        fundmanagerDetails: {
          fundmanagerIds: updatedFundmanagerIds,
          fundmanagers: fundManager
            ?.sort((fund1, fund2) => Number(fund1?.id) - Number(fund2?.id))
            ?.filter((f) => updatedFundmanagerIds.includes(Number(f.id))),
        },
        trusteeDetails: {
          trusteeIds: updatedTrusteeIds,
          trustees: trustee
            ?.sort((trustee1, trustee2) => Number(trustee1?.id) - Number(trustee2?.id))
            ?.filter((f) => updatedTrusteeIds.includes(Number(f.id))),
        },
        requiredFundManagers: Number(requiredFundManagers),
        requiredTrustees: Number(requiredTrustees),
        inActive,
        faceValue,
        fundAddress1,
        fundAddress2,
        fundAddress3,
        isActive,
        custodian,
        // setupFee: setupFee ? setupFee : 0,
        minCommitmentAmount,
        maxCommitmentAmount,
        minContributionPercentage: minContributionPercentage ? minContributionPercentage : 0,
        tcLink,
        distributorIds,
        isApplicableForDirect,
        accreditedInvestorApplicable,
        accreditedMinCommitmentAmount: isAccreditedInvestorApplicable(accreditedInvestorApplicable)
          ? accreditedMinCommitmentAmount
          : null,
        managementFeeApplicable,
        managementFeeType: isFieldApplicable(managementFeeApplicable) ? managementFeeType : null,
        defaultManagementFee:
          isFieldFixed(managementFeeType, managementFeeApplicable) ||
          isFieldVariable(managementFeeType, managementFeeApplicable)
            ? defaultManagementFee
            : null,
        minManagementFee: isFieldVariable(managementFeeType, managementFeeApplicable)
          ? minManagementFee
          : null,
        maxManagementFee: isFieldVariable(managementFeeType, managementFeeApplicable)
          ? maxManagementFee
          : null,
        managementFeeInWords: isFieldWord(managementFeeType, managementFeeApplicable)
          ? managementFeeInWords
          : null,
        hurdleRateApplicable,
        defaultHurdleRate: isFieldApplicable(hurdleRateApplicable) ? defaultHurdleRate : null,
        hurdleRateType: isFieldApplicable(hurdleRateApplicable) ? hurdleRateType : null,
        maxHurdleRate: isFieldVariable(hurdleRateType, hurdleRateApplicable) ? maxHurdleRate : null,
        minHurdleRate: isFieldVariable(hurdleRateType, hurdleRateApplicable) ? minHurdleRate : null,
        performanceFeeApplicable,
        defaultPerformanceFee: isFieldApplicable(performanceFeeApplicable)
          ? defaultPerformanceFee
          : null,
        performanceFeeType: isFieldApplicable(performanceFeeApplicable) ? performanceFeeType : null,
        maxPerformanceFee: isFieldVariable(performanceFeeType, performanceFeeApplicable)
          ? maxPerformanceFee
          : null,
        minPerformanceFee: isFieldVariable(performanceFeeType, performanceFeeApplicable)
          ? minPerformanceFee
          : null,
        exitChargeApplicable,
        defaultExitCharge: isFieldApplicable(exitChargeApplicable) ? defaultExitCharge : null,
        exitChargeType: isFieldApplicable(exitChargeApplicable) ? exitChargeType : null,
        maxExitCharge: isFieldVariable(exitChargeType, exitChargeApplicable) ? maxExitCharge : null,
        minExitCharge: isFieldVariable(exitChargeType, exitChargeApplicable) ? minExitCharge : null,
        managementFee: 0,
        setupFeeApplicable,
        setupFeeType: isFieldApplicable(setupFeeApplicable) ? setupFeeType : null,
        setupFee:
          isFieldFixed(setupFeeType, setupFeeApplicable) ||
          isFieldVariable(setupFeeType, setupFeeApplicable)
            ? setupFee
            : null,
        minSetupFee: isFieldVariable(setupFeeType, setupFeeApplicable) ? minSetupFee : null,
        maxSetupFee: isFieldVariable(setupFeeType, setupFeeApplicable) ? maxSetupFee : null,
        setupFeeInWords: isFieldWord(setupFeeType, setupFeeApplicable) ? setupFeeInWords : null,
        placementFeeApplicable,
        placementFeeType: isFieldApplicable(placementFeeApplicable) ? placementFeeType : null,
        defaultPlacementFee: isFieldApplicable(placementFeeApplicable) ? defaultPlacementFee : 0,
        maxPlacementFee: isFieldVariable(placementFeeType, placementFeeApplicable)
          ? maxPlacementFee
          : null,
        minPlacementFee: isFieldVariable(placementFeeType, placementFeeApplicable)
          ? minPlacementFee
          : null,
        operatingExpenseApplicable,
        operatingExpenseType: isFieldApplicable(operatingExpenseApplicable)
          ? operatingExpenseType
          : null,
        defaultOperatingExpenses:
          isFieldFixed(operatingExpenseType, operatingExpenseApplicable) ||
          isFieldVariable(operatingExpenseType, operatingExpenseApplicable)
            ? defaultOperatingExpenses
            : null,
        minOperatingExpense: isFieldVariable(operatingExpenseType, operatingExpenseApplicable)
          ? minOperatingExpense
          : null,
        maxOperatingExpense: isFieldVariable(operatingExpenseType, operatingExpenseApplicable)
          ? maxOperatingExpense
          : null,
        operatingExpenseInWords: isFieldWord(operatingExpenseType, operatingExpenseApplicable)
          ? operatingExpenseInWords
          : null,
        exitLoadApplicable,
        exitLoad: isFieldApplicable(exitLoadApplicable)
          ? makeStringInSingleLine(exitLoad || '')
          : null,
        transactionCurrency,
      };
      if (!PlanId) {
        await dispatch(addPlan(updatedValues));
      } else {
        await dispatch(updatePlan(Number(PlanId), updatedValues));
      }
      history.push(PlanId ? 'plan-details' : 'view-plans', {
        fundId: fundId,
        PlanId: PlanId,
      });
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  return (
    <>
      <Formik
        initialValues={planInitial}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validationSchema={addPlanValidation}>
        {({ handleSubmit, values, setValues }) => (
          <Box sx={styles.UsersFormikBox} component="form" noValidate onSubmit={handleSubmit}>
            <Hidden smUp={true}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  sx={{ height: 'fit-content', p: 0 }}
                  onClick={() =>
                    history.push(PlanId ? 'plan-details' : 'view-plans', {
                      fundId: fundId,
                      // planId: PlanId,
                      PlanId: PlanId,
                    })
                  }>
                  <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#1C2D47',
                  }}>
                  Back
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  my: 2,
                  py: 2,
                  pl: { xs: 3, sm: 0 },
                  color: 'primary.main',
                  bgcolor: 'rgba(238, 244, 251, 0.5)',
                }}>
                <PersonOutlineOutlinedIcon fontSize="large" />
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    pl: 1,
                  }}>
                  Fund Setup
                </Typography>
              </Box>
            </Hidden>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Hidden only="xs">
                <IconButton
                  sx={{ height: 'fit-content', p: 0 }}
                  onClick={() =>
                    history.push(PlanId ? 'plan-details' : 'view-plans', {
                      fundId: fundId,
                      // planId: PlanId,
                      PlanId: PlanId,
                    })
                  }>
                  <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                </IconButton>
              </Hidden>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#1C2D47',
                  pl: { xs: 3, sm: 0 },
                }}>
                {PlanId ? 'Edit Plan' : 'Add Plan'}
              </Typography>
            </Box>

            {loading ? (
              <LinearProgress sx={{ mt: 2 }} />
            ) : (
              <>
                <Plan
                  values={values}
                  access={access}
                  fundManager={fundManager}
                  trustee={trustee}
                  distributorList={distributorList}
                  setValues={(e) => setValues(e as NewPlanType)}
                />
                {access && (
                  <Box sx={{ width: '100%', maxWidth: '350px', mx: 'auto', mt: 3 }}>
                    <MFSubmitButton label="Save" />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Formik>
    </>
  );
}
